import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard,
  Building2,
  Stethoscope,
  Users,
  CalendarDays,
  Phone,
  Settings,
  LogOut,
  PhoneCall,
  Headphones,
  ExternalLink,
  Clock,
  FileText,
  Brain,
  Download,
  Wallet,
  ChevronDown,
  Cpu,
  Pill,
  X,
  Radio,
  BarChart3,
  PhoneOutgoing,
  Euro,
  Shield,
  MessageCircle
} from 'lucide-react';

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/departments', icon: Building2, label: 'Reparti', needsClinic: true, adminOnly: true },
  { to: '/doctors', icon: Stethoscope, label: 'Medici', needsClinic: true, adminOnly: true },
  { to: '/patients', icon: Users, label: 'Pazienti', needsClinic: true, clinicAccess: true },
  { to: '/appointments', icon: CalendarDays, label: 'Appuntamenti', needsClinic: true, clinicAccess: true },
  { to: '/prescriptions', icon: Pill, label: 'Ricette', needsClinic: true, clinicAccess: true },
  { to: '/chat', icon: MessageCircle, label: 'Chat', needsClinic: true, clinicAccess: true },
  { to: '/waitlist', icon: Clock, label: 'Lista d\'Attesa', needsClinic: true, clinicAccess: true },
  { to: '/live-calls', icon: Radio, label: 'Chiamate Live', clinicAccess: true },
  { to: '/call-logs', icon: PhoneCall, label: 'Log Chiamate', adminOnly: true },
  { to: '/analytics', icon: BarChart3, label: 'Analytics', partnerOnly: true },
  { to: '/recall', icon: PhoneOutgoing, label: 'Recall', partnerOnly: true },
  { to: '/infrastructure', icon: Cpu, label: 'Infrastruttura AI', partnerOnly: true },
  { to: '/clinics', icon: Building2, label: 'Cliniche', partnerOnly: true },
  { to: '/billing', icon: Wallet, label: 'Billing', partnerOnly: true },
  { to: '/costs', icon: Euro, label: 'Costi', partnerOnly: true },
  { to: '/phone-setup', icon: PhoneCall, label: 'Numeri Telefonici', partnerOnly: true },
  { to: '/voice-system', icon: Phone, label: 'Voice AI', adminOnly: true },
  { to: '/ai-settings', icon: Brain, label: 'Istruzioni AI', adminOnly: true },
  { to: '/schedule', icon: Clock, label: 'Orari', adminOnly: true },
  { to: '/onboarding-submissions', icon: FileText, label: 'Onboarding', superadminOnly: true },
  { to: '/compliance-review', icon: FileText, label: 'Verifica Documenti', superadminOnly: true },
  { to: '/security', icon: Shield, label: 'Sicurezza 2FA' },
  { to: '/settings', icon: Settings, label: 'Impostazioni', adminOnly: true }
];

export default function Sidebar({ isOpen, onClose }) {
  const { user, logout, isAdmin, isPartner, myClinics, activeClinic, switchClinic } = useAuth();
  const navigate = useNavigate();
  const [clinicDropdownOpen, setClinicDropdownOpen] = React.useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleNavClick = () => {
    if (onClose) onClose();
  };

  const hasActiveClinic = !!activeClinic || (user?.clinic_id && user.clinic_id !== '');
  const userRole = user?.role;

  const filteredItems = navItems.filter(item => {
    if (item.superadminOnly && userRole !== 'superadmin') return false;
    if (item.partnerOnly && !['superadmin', 'partner'].includes(userRole)) return false;
    if (userRole === 'receptionist') {
      return item.clinicAccess || item.to === '/';
    }
    if (userRole === 'doctor') {
      return item.clinicAccess || item.to === '/';
    }
    if (item.needsClinic) {
      if (userRole === 'partner' && !hasActiveClinic) return false;
      if (userRole === 'partner') return true;
    }
    if (item.adminOnly && !isAdmin) return false;
    return true;
  });

  // PWA Install
  const [installPrompt, setInstallPrompt] = React.useState(null);
  const [isInstalled, setIsInstalled] = React.useState(false);

  React.useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => setIsInstalled(true));
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const result = await installPrompt.userChoice;
      if (result.outcome === 'accepted') setIsInstalled(true);
      setInstallPrompt(null);
    }
  };

  return (
    <aside className={`sidebar ${isOpen ? 'open' : ''}`} data-testid="sidebar">
      {/* Logo */}
      <div className="px-6 py-5 border-b border-slate-200/60 shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-sky-500 rounded-lg flex items-center justify-center shrink-0">
              <Phone className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-slate-800 text-lg" style={{ fontFamily: 'Manrope' }}>
                VoiceBook
              </h1>
              <p className="text-xs text-slate-500">Centro Medico</p>
            </div>
          </div>
          {/* Close button for mobile */}
          {onClose && (
            <button
              onClick={onClose}
              className="md:hidden p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
              data-testid="sidebar-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Clinic Selector for Partners */}
      {(isPartner || user?.role === 'superadmin') && myClinics.length > 0 && (
        <div className="px-3 pt-3 pb-1 shrink-0" data-testid="clinic-selector">
          <div className="relative">
            <button
              onClick={() => setClinicDropdownOpen(!clinicDropdownOpen)}
              className="w-full flex items-center justify-between gap-2 px-3 py-2.5 bg-sky-50 border border-sky-200 rounded-lg hover:bg-sky-100 transition-colors text-left"
              data-testid="clinic-selector-btn"
            >
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-medium text-sky-500 uppercase tracking-wider">Clinica Attiva</p>
                <p className="text-sm font-semibold text-slate-800 truncate" data-testid="active-clinic-name">
                  {activeClinic?.name || 'Seleziona clinica...'}
                </p>
                {activeClinic?.phone_number ? (
                  <p className="text-[11px] text-emerald-600 font-mono mt-0.5 flex items-center gap-1">
                    <Phone className="w-3 h-3" />
                    {activeClinic.phone_number}
                  </p>
                ) : (
                  <p className="text-[10px] text-amber-500 mt-0.5">Nessun numero assegnato</p>
                )}
                {activeClinic?.trial_mode && (
                  <p className={`text-[10px] font-semibold mt-0.5 ${activeClinic.trial_days_left <= 0 ? 'text-red-500' : activeClinic.trial_days_left <= 2 ? 'text-red-500' : 'text-amber-600'}`} data-testid="active-clinic-trial">
                    {activeClinic.trial_days_left <= 0 ? 'DEMO SCADUTA' : `DEMO: ${activeClinic.trial_days_left}g rimasti`}
                  </p>
                )}
              </div>
              <ChevronDown className={`w-4 h-4 text-sky-400 transition-transform ${clinicDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {clinicDropdownOpen && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto" data-testid="clinic-dropdown">
                {myClinics.map((clinic) => (
                  <button
                    key={clinic.id}
                    onClick={() => {
                      if (clinic.id !== activeClinic?.id) {
                        switchClinic(clinic.id);
                      }
                      setClinicDropdownOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2.5 text-sm hover:bg-sky-50 transition-colors border-b border-slate-100 last:border-0 ${
                      clinic.id === activeClinic?.id ? 'bg-sky-50 text-sky-700 font-medium' : 'text-slate-700'
                    }`}
                    data-testid={`clinic-option-${clinic.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <p className="font-medium truncate flex-1">{clinic.name}</p>
                      {clinic.trial_mode && (
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${
                          clinic.trial_days_left <= 0 ? 'bg-red-100 text-red-600' :
                          clinic.trial_days_left <= 2 ? 'bg-red-100 text-red-600' :
                          'bg-amber-100 text-amber-600'
                        }`}>
                          {clinic.trial_days_left <= 0 ? 'SCADUTA' : `${clinic.trial_days_left}g`}
                        </span>
                      )}
                    </div>
                    {clinic.phone_number ? (
                      <p className="text-[10px] text-emerald-500 font-mono">{clinic.phone_number}</p>
                    ) : (
                      <p className="text-[10px] text-slate-400">Nessun numero</p>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* No clinic warning for partners */}
      {isPartner && myClinics.length === 0 && (
        <div className="px-3 pt-3 shrink-0">
          <div className="px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs text-amber-700 font-medium">Nessuna clinica</p>
            <p className="text-[10px] text-amber-600">Vai su Cliniche per crearne una</p>
          </div>
        </div>
      )}

      {/* Navigation - scrollable area */}
      <nav className="py-3 flex-1 overflow-y-auto" data-testid="sidebar-nav">
        {filteredItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            onClick={handleNavClick}
            className={({ isActive }) =>
              `sidebar-nav-item ${isActive ? 'active' : ''}`
            }
            data-testid={`nav-${item.label.toLowerCase().replace(/[ ']/g, '-')}`}
          >
            <item.icon className="w-5 h-5 shrink-0" />
            <span>{item.label}</span>
          </NavLink>
        ))}
        
        {/* Support CTA */}
        <div className="mx-2 mt-3 pt-3 border-t border-slate-200/60">
          <a
            href="https://telcavoip.ai/en/area-clienti/login"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-4 py-2.5 bg-gradient-to-r from-sky-500 to-sky-600 text-white rounded-lg hover:from-sky-600 hover:to-sky-700 transition-all shadow-sm hover:shadow-md"
            data-testid="support-cta"
          >
            <Headphones className="w-5 h-5 shrink-0" />
            <div className="flex-1">
              <span className="text-sm font-medium block">Richiedi Supporto</span>
              <span className="text-xs text-sky-100">TelcaVoIP AI</span>
            </div>
            <ExternalLink className="w-4 h-4 text-sky-200" />
          </a>

          {/* PWA Install Button */}
          {!isInstalled && (
            <button
              onClick={handleInstall}
              className="w-full mt-2 flex items-center gap-3 px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-lg hover:from-emerald-600 hover:to-emerald-700 transition-all shadow-sm hover:shadow-md"
              data-testid="install-pwa-btn"
            >
              <Download className="w-5 h-5 shrink-0" />
              <div className="flex-1 text-left">
                <span className="text-sm font-medium block">Installa App</span>
                <span className="text-xs text-emerald-100">Accesso rapido</span>
              </div>
            </button>
          )}
        </div>
      </nav>

      {/* User section - always visible at bottom */}
      <div className="border-t border-slate-200/60 p-4 bg-white shrink-0">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-9 h-9 bg-slate-100 rounded-full flex items-center justify-center shrink-0">
            <span className="text-sm font-medium text-slate-600">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-800 truncate">{user?.name}</p>
            <p className="text-xs text-slate-500 truncate">{user?.role}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          data-testid="logout-button"
        >
          <LogOut className="w-4 h-4" />
          <span>Esci</span>
        </button>
      </div>
    </aside>
  );
}
